import { useEmailFeature } from './email'
import { useRole } from './team'

// What the Ctrl+K search can jump to on the Settings page. `section` is the
// card heading; `label` is the visible label of the field. The Settings page
// finds both by their on-screen text (see useFindOnPage), so a field
// that is hidden for this user or this state (e.g. the late fee rate while
// late fees are off) still lands on its card.
export interface SettingsEntry {
  section: string
  label: string
  keywords?: string
  // Who sees the card. Mirrors the conditions in pages/Settings.tsx.
  tab: 'business' | 'reminders' | 'team' | 'billing' | 'account'
  needs?: 'email' | 'team' | 'billing' | 'owner'
}

export const SETTINGS_ENTRIES: SettingsEntry[] = [
  { tab: 'business', section: 'Business', label: 'Business name', keywords: 'company rename organisation organization' },
  { tab: 'business', section: 'Business', label: 'Address', keywords: 'postal street' },
  { tab: 'business', section: 'Customer contact page', label: 'Contact person', keywords: 'invoice link how to pay bank details' },
  { tab: 'reminders', section: 'Alerts', label: 'Send me alerts', keywords: 'email notify owner summary' },
  { tab: 'reminders', section: 'Payment terms', label: 'Payment terms (days)', keywords: 'due net days' },
  { tab: 'reminders', section: 'Payment terms', label: 'Last reminder (days overdue)', keywords: 'final period legal referral final notice' },
  { tab: 'reminders', section: 'Late fees', label: 'Late fee rate (% of invoice)', keywords: 'interest late fees' },
  { tab: 'reminders', section: 'Late fees', label: 'Turn on late fees and interest', keywords: 'national credit act ncr' },
  { tab: 'reminders', section: 'WhatsApp consent', label: 'Declare existing WhatsApp consent', keywords: 'opt-in popia consent' },
  { tab: 'reminders', section: 'Email reminders', label: 'Reply-to email', keywords: 'reply address', needs: 'email' },
  { tab: 'reminders', section: 'Email reminders', label: 'Your contact name', keywords: 'email sender', needs: 'email' },
  { tab: 'team', section: 'Team', label: 'Invite someone', keywords: 'add user member role invite', needs: 'team' },
  { tab: 'billing', section: 'Billing', label: 'Billing', keywords: 'subscription plan cancel card invoice usage allowance', needs: 'billing' },
  { tab: 'account', section: 'Account', label: 'Change password', keywords: 'security login' },
  { tab: 'account', section: 'Danger zone', label: 'Delete my business data', keywords: 'delete organisation close account remove organization', needs: 'owner' },
]

/** The entries this user can actually reach on the Settings page. */
export function useSettingsEntries(): SettingsEntry[] {
  const { isOwner, canSeeBilling, teamEnabled } = useRole()
  const emailOn = useEmailFeature()
  const allowed = { email: emailOn, team: teamEnabled, billing: canSeeBilling, owner: isOwner }
  return SETTINGS_ENTRIES.filter((entry) => !entry.needs || allowed[entry.needs])
}
